"use client";

import {useEffect, useRef, useState} from "react";
import {useRouter} from "@bprogress/next";
import {createDraftWorkflow} from "@/actions/workflows/actions";
import {
   runAfterCallWorkflowBuild,
   type AfterCallBuildFailure,
   type AfterCallBuildStage,
} from "@/components/workflows/ai-chat/headlessAfterCallBuild";
import {
   buildAfterCallReturnInput,
   writeWorkflowBuildSeed,
   type TranslateFn,
} from "@/components/workflows/ai-chat/workflowBuildSeed";
import {shouldAutoStartWorkflowHandoff} from "@/services/wingman/buildWorkflowContent";
import type {MessageContentBuildWorkflow} from "@/services/wingman";
import {
   claimHandoff,
   isHandoffClaimedElsewhere,
   isHandoffKey,
   readHandoffNotified,
   readHandoffPublished,
   readLinkedWorkflowId,
   releaseHandoff,
   writeHandoffNotified,
   writeHandoffPublished,
   writeLinkedWorkflowId,
} from "./handoffStorage";

export type HandoffStatus =
   | "idle"
   | AfterCallBuildStage
   | "navigating"
   | "published"
   /** Another tab holds the claim and is driving this build. */
   | "claimed"
   | "error";

export const IN_PROGRESS_STATUSES: ReadonlySet<HandoffStatus> = new Set<HandoffStatus>([
   "creating",
   "building",
   "publishing",
   "navigating",
]);

/**
 * Sends the continuation message once an after-call build has published.
 * Resolving `false` means the chat refused it (a stream was active); the hook
 * keeps the message queued and retries once `canSendContinuation` allows.
 */
export type BuildPublishedHandler = (
   messageText: string
) => void | boolean | Promise<void | boolean>;

/** Fallback retry when a send is refused while the chat still looks idle. */
export const CONTINUATION_RETRY_MS = 1500;

export function builderUrl(workflowId: string, aiBuild = false): string {
   return `/workflows/${workflowId}/builder${aiBuild ? "?aiBuild=1" : ""}`;
}

interface PublishedWorkflow {
   id: string;
   name: string;
}

export interface UseWorkflowBuildHandoffParams {
   buildWorkflow: MessageContentBuildWorkflow;
   isUser?: boolean;
   /** Whether the assistant turn has finished streaming; gates the button and the auto-start. */
   canApprove: boolean;
   /** Whether the chat can take the continuation now (idle, and this card still ends the conversation). */
   canSendContinuation: boolean;
   /** Assistant this handoff came from; the tool attaches to and returns to it. */
   originAssistantId?: string;
   onBuildPublished?: BuildPublishedHandler;
   /** `ask` namespace translator; phrases the continuation in the operator's language. */
   t: TranslateFn;
}

export interface WorkflowBuildHandoff {
   status: HandoffStatus;
   failure: AfterCallBuildFailure | null;
   linkedWorkflowId: string | null;
   alreadyPublished: boolean;
   isInProgress: boolean;
   openWorkflow: (workflowId: string) => void;
   /** Background path for after-call automations; resumes a linked draft when retrying. */
   startBackgroundBuild: () => Promise<void>;
   /** Manual path for callable tools: create the draft and open the builder. */
   startBuilderHandoff: () => Promise<void>;
}

/**
 * Orchestrates a `build_workflow` handoff card: hydrates its durable state,
 * takes the cross-tab claim before creating a draft, drives the headless
 * after-call build (linking the draft as soon as it exists), and queues the
 * continuation message until the chat accepts it.
 */
export function useWorkflowBuildHandoff({
   buildWorkflow,
   isUser = false,
   canApprove,
   canSendContinuation,
   originAssistantId,
   onBuildPublished,
   t,
}: UseWorkflowBuildHandoffParams): WorkflowBuildHandoff {
   const router = useRouter();
   const [status, setStatus] = useState<HandoffStatus>("idle");
   const [failure, setFailure] = useState<AfterCallBuildFailure | null>(null);
   const [linkedWorkflowId, setLinkedWorkflowId] = useState<string | null>(null);
   const [alreadyPublished, setAlreadyPublished] = useState(false);
   const [pendingContinuation, setPendingContinuation] = useState<PublishedWorkflow | null>(null);
   const [continuationRetry, setContinuationRetry] = useState(0);
   const startedRef = useRef(false);
   const sendingContinuationRef = useRef(false);
   // Ref-bridged: ChatView recreates the callback every render and `t` is not
   // guaranteed stable, and neither should restart the continuation effect.
   const onBuildPublishedRef = useRef(onBuildPublished);
   onBuildPublishedRef.current = onBuildPublished;
   const tRef = useRef(t);
   tRef.current = t;

   const {callId, workflow} = buildWorkflow;
   const title = workflow.suggestedName?.trim() || workflow.description;

   // Hydrate from storage after mount (kept out of the initializer to avoid a
   // server/client hydration mismatch) and mirror what other tabs write. Only
   // the mount pass may queue a continuation: a live `storage` event means the
   // tab that published is already sending it.
   useEffect(() => {
      const sync = (fromMount: boolean) => {
         const existing = readLinkedWorkflowId(callId);
         if (existing) setLinkedWorkflowId(existing);
         const published = readHandoffPublished(callId);
         if (published) setAlreadyPublished(true);
         if (fromMount && published && existing && !readHandoffNotified(callId)) {
            setPendingContinuation((current) => current ?? {id: existing, name: title});
         }
         setStatus((current) => {
            if (published || (current !== "idle" && current !== "claimed")) return current;
            return isHandoffClaimedElsewhere(callId) ? "claimed" : "idle";
         });
      };
      sync(true);
      const onStorage = (event: StorageEvent) => {
         if (event.key === null || isHandoffKey(event.key, callId)) sync(false);
      };
      window.addEventListener("storage", onStorage);
      return () => window.removeEventListener("storage", onStorage);
   }, [callId, title]);

   const openWorkflow = (workflowId: string) => {
      router.push(builderUrl(workflowId));
   };

   const linkDraft = (workflowId: string) => {
      writeLinkedWorkflowId(callId, workflowId);
      setLinkedWorkflowId(workflowId);
   };

   /** Takes the cross-tab claim; marks the card as driven elsewhere when it cannot. */
   const beginHandoff = async (): Promise<boolean> => {
      if (isUser || startedRef.current) return false;
      startedRef.current = true;
      if (await claimHandoff(callId)) return true;
      startedRef.current = false;
      setStatus("claimed");
      return false;
   };

   const endHandoff = () => {
      releaseHandoff(callId);
      startedRef.current = false;
   };

   const startBackgroundBuild = async () => {
      if (!(await beginHandoff())) return;
      setFailure(null);
      try {
         const result = await runAfterCallWorkflowBuild({
            name: workflow.suggestedName?.trim() || title,
            prompt: workflow.description,
            existingWorkflowId: readLinkedWorkflowId(callId) ?? undefined,
            onStage: (stage) => setStatus(stage),
            // Persisted before the long stream so a reload or a thrown fetch
            // still finds the draft; the catch below never clears it.
            onDraftCreated: linkDraft,
         });
         if (result.ok) {
            linkDraft(result.workflow.id);
            writeHandoffPublished(callId);
            setAlreadyPublished(true);
            setStatus("published");
            setPendingContinuation(result.workflow);
            return;
         }
         if (result.workflowId) linkDraft(result.workflowId);
         if (result.detail) {
            console.error(`[build_workflow] after-call build failed (${result.reason}):`, result.detail);
         }
         setFailure(result.reason);
         setStatus("error");
      } catch (error) {
         console.error("[build_workflow] after-call build threw:", error);
         setFailure("builder");
         setStatus("error");
      } finally {
         endHandoff();
      }
   };

   const startBuilderHandoff = async () => {
      if (isUser || startedRef.current) return;
      const existing = readLinkedWorkflowId(callId);
      if (existing) {
         setLinkedWorkflowId(existing);
         openWorkflow(existing);
         return;
      }
      if (!(await beginHandoff())) return;
      try {
         setStatus("creating");
         const result = await createDraftWorkflow(
            workflow.suggestedName?.trim() || "Untitled Workflow"
         );
         if (!result.data?.id) {
            setStatus("error");
            return;
         }
         writeWorkflowBuildSeed(result.data.id, {
            prompt: workflow.description,
            autoPublish: true,
            purpose: workflow.purpose,
            returnTo: "gailgpt",
            ...(originAssistantId ? {originAssistantId} : {}),
         });
         linkDraft(result.data.id);
         setStatus("navigating");
         router.push(builderUrl(result.data.id, true));
      } catch {
         setStatus("error");
      } finally {
         endHandoff();
      }
   };

   useEffect(() => {
      // Read storage directly: the hydration effect above has not re-rendered
      // yet on the first pass, so state would still say "not linked" for a
      // card whose build already ran (or is half done) in another visit.
      const alreadyLinked =
         Boolean(linkedWorkflowId) ||
         alreadyPublished ||
         readLinkedWorkflowId(callId) !== null ||
         readHandoffPublished(callId);
      if (
         !shouldAutoStartWorkflowHandoff({
            purpose: workflow.purpose,
            canApprove,
            alreadyLinked,
         })
      ) {
         return;
      }
      // The guards inside make any re-run a no-op.
      void startBackgroundBuild();
   }, [callId, canApprove, linkedWorkflowId, alreadyPublished, workflow.purpose]);

   /** Hands the continuation to the chat; false when there is no sender, it refused, or it threw. */
   const deliverContinuation = async (published: PublishedWorkflow): Promise<boolean> => {
      const send = onBuildPublishedRef.current;
      if (!send) return false;
      try {
         return (await send(buildAfterCallReturnInput(tRef.current, published))) !== false;
      } catch (error) {
         console.error("[build_workflow] continuation send threw:", error);
         return false;
      }
   };

   const bumpContinuationRetry = () => setContinuationRetry((count) => count + 1);

   // Deliver the queued continuation once the chat can take it. The chat
   // refuses a send while a stream is active; the message then stays queued
   // and goes out when `canSendContinuation` flips back (or after a fallback
   // delay). The `-notified` marker is written only after acceptance, so a
   // reload never re-sends a delivered message but does retry a dropped one.
   useEffect(() => {
      if (!pendingContinuation || !canSendContinuation || sendingContinuationRef.current) return;
      if (!onBuildPublishedRef.current) return;
      if (readHandoffNotified(callId)) {
         setPendingContinuation(null);
         return;
      }
      sendingContinuationRef.current = true;
      let retryTimer: ReturnType<typeof setTimeout> | undefined;
      let disposed = false;
      void deliverContinuation(pendingContinuation).then((accepted) => {
         sendingContinuationRef.current = false;
         if (accepted) {
            writeHandoffNotified(callId);
            setPendingContinuation(null);
            return;
         }
         if (!disposed) retryTimer = setTimeout(bumpContinuationRetry, CONTINUATION_RETRY_MS);
      });
      return () => {
         disposed = true;
         if (retryTimer) clearTimeout(retryTimer);
      };
   }, [pendingContinuation, canSendContinuation, continuationRetry, callId]);

   return {
      status,
      failure,
      linkedWorkflowId,
      alreadyPublished,
      isInProgress: IN_PROGRESS_STATUSES.has(status),
      openWorkflow,
      startBackgroundBuild,
      startBuilderHandoff,
   };
}
